import { useState } from "react";
import axios from "axios";
import { CheckCircle } from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";
import { Nav, Footer, WhatsAppButton } from "@/components/Chrome";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { BRAND } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["09:00", "09:45", "10:30", "11:15", "13:00", "13:45", "14:30", "15:15", "16:00", "16:45"];

const inputCls = "h-12 w-full border border-white/15 bg-black px-4 text-sm text-white placeholder:text-white/30 transition-colors focus:border-white/45 focus:outline-none";
const labelCls = "mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-white/45";

export default function BookPage() {
    const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", date: "", time: "", notes: "" });
    const [state, setState] = useState("idle");
    const today = new Date().toISOString().split("T")[0];

    const submit = async (e) => {
        e.preventDefault();
        setState("sending");
        try {
            await axios.post(`${API}/bookings`, form);
            setState("success");
        } catch {
            setState("error");
        }
    };

    return (
        <div className="min-h-screen bg-base" data-testid="book-page">
            <SEO title="Book a Free Consultation | HIGHTS" description="Book a free 10–20 minute HIGHTS consultation. We'll review your online presence and show you where AI, automation and development could create growth." path="/book" />
            <Nav />
            <main className="mx-auto max-w-[1400px] px-5 pb-28 pt-32 md:px-10">
                <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/40">Free · 10–20 minutes · No obligation</p>
                        <h1 className="mt-6 font-display font-black tracking-tighter" data-testid="book-headline">
                            <MaskedLine delay={0.1}><span className="block text-4xl leading-[0.98] text-white sm:text-5xl">SEE WHAT HIGHTS</span></MaskedLine>
                            <MaskedLine delay={0.25}><span className="block text-4xl leading-[0.98] text-white/50 sm:text-5xl">COULD BUILD FOR YOU.</span></MaskedLine>
                        </h1>
                        <Reveal delay={0.35}>
                            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-white/60">
                                We'll look at your current online presence, understand your goals and show you where
                                automation, AI, development and optimisation could create opportunities.
                            </p>
                            <ul className="mt-8 space-y-3 text-sm font-light text-white/55">
                                {["An honest review of your current website and visibility", "Where enquiries are leaking — and how systems fix it", "A clear, no-pressure view of what a build would involve"].map((s) => (
                                    <li key={s} className="flex items-start gap-3"><span className="mt-2 h-1 w-1 shrink-0 bg-white/50" />{s}</li>
                                ))}
                            </ul>
                            <p className="mt-10 border-t border-white/10 pt-6 text-sm text-white/50">
                                Prefer to message? <a className="text-white hover:underline" href={`https://wa.me/${BRAND.phoneIntl}?text=${encodeURIComponent(BRAND.whatsappMsg)}`} target="_blank" rel="noopener noreferrer" data-testid="book-whatsapp-link">WhatsApp {BRAND.phoneDisplay}</a>
                            </p>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15} className="lg:col-span-7">
                        <div className="border border-white/12 bg-surface p-7 md:p-10">
                            {state === "success" ? (
                                <div className="py-10 text-center" data-testid="booking-success">
                                    <CheckCircle size={44} weight="fill" className="mx-auto text-white" />
                                    <p className="mt-6 font-display text-3xl font-black tracking-tight text-white">Consultation booked.</p>
                                    <p className="mx-auto mt-4 max-w-sm text-sm font-light leading-relaxed text-white/55">
                                        {form.date} at {form.time}. A confirmation is on its way to {form.email} —
                                        and the HIGHTS team has been notified.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                        <div>
                                            <label className={labelCls} htmlFor="bk-name">Name *</label>
                                            <input id="bk-name" data-testid="booking-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Your name" />
                                        </div>
                                        <div>
                                            <label className={labelCls} htmlFor="bk-business">Business</label>
                                            <input id="bk-business" data-testid="booking-business" value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} className={inputCls} placeholder="Business name" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                        <div>
                                            <label className={labelCls} htmlFor="bk-email">Email *</label>
                                            <input id="bk-email" data-testid="booking-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@company.com" />
                                        </div>
                                        <div>
                                            <label className={labelCls} htmlFor="bk-phone">Phone</label>
                                            <input id="bk-phone" data-testid="booking-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="+44…" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className={labelCls} htmlFor="bk-date">Preferred date *</label>
                                        <input id="bk-date" data-testid="booking-date" required type="date" min={today} style={{ colorScheme: "dark" }} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
                                    </div>
                                    <div>
                                        <span className={labelCls}>Time *</span>
                                        <div className="grid grid-cols-5 gap-2" data-testid="booking-slots">
                                            {SLOTS.map((t) => (
                                                <button key={t} type="button" onClick={() => setForm({ ...form, time: t })}
                                                    data-testid={`booking-slot-${t.replace(":", "")}`}
                                                    className={`border py-2.5 font-mono text-xs transition-colors ${form.time === t ? "border-white bg-white text-black" : "border-white/15 text-white/55 hover:border-white/45 hover:text-white"}`}>
                                                    {t}
                                                </button>
                                            ))}
                                        </div>
                                        {!form.time && state === "error" && <p className="mt-2 text-xs text-white/50">Please choose a time.</p>}
                                    </div>
                                    <div>
                                        <label className={labelCls} htmlFor="bk-notes">Anything we should know?</label>
                                        <textarea id="bk-notes" data-testid="booking-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                            className="w-full border border-white/15 bg-black px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-white/45 focus:outline-none"
                                            placeholder="Your current website, biggest challenge, goals…" />
                                    </div>
                                    <button type="submit" disabled={state === "sending" || !form.time} data-testid="booking-submit-button"
                                        className="w-full bg-white py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-white/85 disabled:opacity-40">
                                        {state === "sending" ? "Booking…" : "Book my consultation"}
                                    </button>
                                    {state === "error" && <p className="text-center text-xs text-white/50" data-testid="booking-error">Something went wrong — please try again or WhatsApp us.</p>}
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </main>
            <Footer />
            <WhatsAppButton />
        </div>
    );
}
